import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { EMableEventName } from '@/core/constants/event_types/events';
import { StorageHandler } from '@/core/data_handlers/storage_handler';
import { Logger } from '@/shared/logger/logger';

import { AbstractLeadFormEventTrigger } from './abstract_lead_event';

export type CustomerDataDetectWtagInput = {
  email: string[];
  eventType?: 'lead_form' | 'login' | 'register';
  firstName: string[];
  lastName: string[];
  phone: string[];
};

type CustomerInfo = Record<string, string[]>;

export class LeadFormEventTrigger extends AbstractLeadFormEventTrigger {
  static leadEventBufferKey = 'leadEventBuffer';
  static _storageHandler = new StorageHandler<CustomerDataDetectWtagInput[]>(
    this.leadEventBufferKey,
    [],
    EBrowserStorageType.LOCALSTORAGE,
  );

  private detectFormType(
    form: HTMLFormElement,
  ): 'lead_form' | 'login' | 'register' {
    const path = window.location.pathname.toLowerCase();
    const formAction = (form.getAttribute('action') || '').toLowerCase();
    const formId = (form.getAttribute('id') || '').toLowerCase();
    const formClass = (form.getAttribute('class') || '').toLowerCase();

    // Check URL patterns first
    if (
      path.includes('/account/register') ||
      path.includes('/signup') ||
      path.includes('/register')
    ) {
      return 'register';
    }
    if (
      path.includes('/account/login') ||
      path.includes('/signin') ||
      path.includes('/login')
    ) {
      return 'login';
    }

    // Check form attributes
    const loginIndicators = ['login', 'signin', 'sign-in'];
    const registerIndicators = [
      'register',
      'signup',
      'sign-up',
      'create-account',
    ];

    const formText = `${formAction} ${formId} ${formClass}`;

    // Check register indicators first
    if (registerIndicators.some(indicator => formText.includes(indicator))) {
      return 'register';
    }
    // Then check login indicators
    if (loginIndicators.some(indicator => formText.includes(indicator))) {
      return 'login';
    }

    // Field-based heuristics
    const passwordFields = form.querySelectorAll('input[type="password"]');
    const emailFields = form.querySelectorAll('input[type="email"]');
    const nameFields = form.querySelectorAll(
      'input[name*="name" i], input[name*="firstName" i], input[name*="lastName" i]',
    );

    // Register form typically has name fields and may have multiple password fields (password + confirm)
    if (
      passwordFields.length > 0 &&
      (nameFields.length > 0 ||
        formText.includes('create-account') ||
        formText.includes('sign-up'))
    ) {
      return 'register';
    }

    // Login form typically has exactly one password field and one email field, but no name fields
    if (
      passwordFields.length === 1 &&
      emailFields.length === 1 &&
      nameFields.length === 0
    ) {
      return 'login';
    }

    // Default to lead form if no other conditions match
    return 'lead_form';
  }

  handleFormSubmit = async (
    e: SubmitEvent,
  ): Promise<CustomerDataDetectWtagInput> => {
    const form = e.target as HTMLFormElement;
    const customerInfo: CustomerInfo = {};
    const customerInfoSearchQuery = Object.entries(
      LeadFormEventTrigger.customerDataMap,
    );

    for (const [key, selectors] of customerInfoSearchQuery) {
      customerInfo[key] = [];
      for (const selector of selectors) {
        const inputs = form.querySelectorAll(selector);
        inputs.forEach((input: Element) => {
          const inputElement = input as HTMLInputElement;
          if (inputElement.value) {
            customerInfo[key]?.push(inputElement.value);
          }
        });
      }
      for (const custInfoKey in customerInfo) {
        customerInfo[custInfoKey] = Array.from(
          new Set(customerInfo[custInfoKey]?.flat()),
        );
      }
    }

    const eventType = this.detectFormType(form);
    Logger.debug('Form event type detected', { eventType });

    return {
      email: customerInfo['email'] || [],
      eventType,
      firstName: customerInfo['firstName'] || [],
      lastName: customerInfo['lastName'] || [],
      phone: customerInfo['phone'] || [],
    };
  };

  override registerEvent() {
    const forms = document.querySelectorAll('form');
    const customerDataForms = Array.from(forms).filter(form =>
      Object.values(LeadFormEventTrigger.customerDataMap).some(selectors =>
        selectors.some(selector => form.querySelector(selector)),
      ),
    );
    Logger.debug('CUSTOMER DATA FORMS', { customerDataForms });

    const currBuffer = LeadFormEventTrigger._storageHandler.get() ?? [];
    Logger.debug('Lead event buffer', currBuffer);
    for (const leadCustomerData of currBuffer) {
      Logger.debug('Lead event initiated', leadCustomerData);

      if (
        leadCustomerData.eventType === 'register' ||
        leadCustomerData.eventType === 'login'
      ) {
        this.wtagFunction('event', EMableEventName.CUSTOM_EVENT, {
          cdas: [
            {
              ds: leadCustomerData.eventType + '_form',
              em: leadCustomerData.email[0] || '',
              fn: leadCustomerData.firstName[0] || '',
              ln: leadCustomerData.lastName[0] || '',
              ph: leadCustomerData.phone[0] || '',
            },
          ],
          eventData: {
            cen: leadCustomerData.eventType,
          },
        });
      } else {
        this.handleEventTriggered<CustomerDataDetectWtagInput>(
          leadCustomerData,
          EMableEventName.LEAD,
        );
      }
    }
    LeadFormEventTrigger._storageHandler.set([]);

    customerDataForms.forEach(form => {
      form.addEventListener('submit', async e => {
        const customerInfo = await this.handleFormSubmit(e);

        if (
          customerInfo.email.length > 0 ||
          customerInfo.phone.length > 0 ||
          customerInfo.firstName.length > 0 ||
          customerInfo.lastName.length > 0
        ) {
          const currBufferLS = LeadFormEventTrigger._storageHandler.get() ?? [];
          currBufferLS.push(customerInfo);
          LeadFormEventTrigger._storageHandler.set(currBufferLS);
        } else {
          Logger.warn('No customer data detected in form event');
        }
      });
    });
  }
}
